import { Box } from "@mui/material";
import React, { useEffect } from "react";
import FileUploadZone from "../../../components/inputs/FileUpload/FileUploadZone";
import {
  MAX_FILE_SIZE,
  SUPPORTED_EXTENSIONS,
  SUPPORTED_MIME_TYPES,
} from "../../../config";
import Translations from "../../../lang/en/bulk.json";
import { useError } from "../../../contexts/ErrorContext";
import { CaseUploadError, CaseUploadItem, FileStatus } from "./index.m";

const MAX_CASES = 10;
const MAX_FILES_PER_CASE = 10;

// Updated error type to handle both case-level and file-level errors

interface BulkFileUploadProps {
  onChange?: (
    cases: CaseUploadItem[],
    errors: CaseUploadError[],
    filesWithoutCase: string[]
  ) => void;
}

const BulkFileUpload: React.FC<BulkFileUploadProps> = ({ onChange }) => {
  const [cases, setCases] = React.useState<CaseUploadItem[]>([]);
  const [errors, setErrors] = React.useState<CaseUploadError[]>([]);
  const [filesWithoutCase, setFilesWithoutCase] = React.useState<File[]>([]);

  const { showError } = useError();

  const validateFileExtension = (file: File): boolean => {
    const ext = file.name.toLowerCase().split(".").pop();
    return SUPPORTED_EXTENSIONS.includes(ext || "");
  };

  const validateFileMime = (file: File): boolean =>
    SUPPORTED_MIME_TYPES.includes(file.type);

  const validate = React.useCallback(() => {
    const allErrors: CaseUploadError[] = [];

    cases.forEach((caseItem, caseIndex) => {
      const caseError: CaseUploadError = {};
      const fileErrors: Array<{ filename: string; errors: string[] }> = [];
      let hasOversizedFile = false;
      let validFileCount = 0;

      // Check if this case exceeds MAX_CASES limit
      if (caseIndex >= MAX_CASES) {
        caseError.caseError =
          Translations.upload.errors.maxCasesExceeded?.replace(
            "{maxCases}",
            MAX_CASES.toString()
          );
        allErrors[caseIndex] = caseError;
        return; // Skip further validation for this case
      }

      // Validate case-level
      if (caseItem.files.length === 0) {
        caseError.caseError = Translations.upload.errors.empty;
      } else if (caseItem.files.length > MAX_FILES_PER_CASE) {
        caseError.caseError =
          Translations.upload.errors.maxFilesPerCase.replace(
            "{maxFiles}",
            MAX_FILES_PER_CASE.toString()
          );
      }

      // Validate individual files
      caseItem.files.forEach((caseFile) => {
        const fileErrorList: string[] = [];

        if (
          !validateFileExtension(caseFile.file) ||
          !validateFileMime(caseFile.file)
        ) {
          const extError = Translations.upload.errors.extension.replace(
            "{supportedFormats}",
            SUPPORTED_EXTENSIONS.map((f) => f.toUpperCase()).join(" ")
          );
          fileErrorList.push(extError);
        }

        if (caseFile.file.size > MAX_FILE_SIZE) {
          if (fileErrorList.length === 0) {
            hasOversizedFile = true;
          }
          fileErrorList.push(
            Translations.upload.errors.size.replace(
              "{maxSize}",
              (MAX_FILE_SIZE / (1024 * 1024)).toString()
            )
          );
        }

        if (fileErrorList.length > 0) {
          fileErrors.push({
            filename: caseFile.file.name,
            errors: fileErrorList,
          });
        } else {
          // This file is valid
          validFileCount++;
        }
      });

      // If any file is too large, set a case-level error
      if (hasOversizedFile && !caseError.caseError) {
        caseError.caseError =
          Translations.upload.errors.caseContainsOversizedFile.replace(
            "{maxSize}",
            (MAX_FILE_SIZE / (1024 * 1024)).toString()
          ) ||
          "This case contains files that exceed the maximum file size limit";
      }

      // If no valid files remain, set a case-level error
      if (
        validFileCount === 0 &&
        !caseError.caseError &&
        caseItem.files.length > 0
      ) {
        caseError.caseError =
          Translations.upload.errors.noValidFiles ||
          "This case has no valid files to upload";
      }

      // Add errors if any exist
      if (caseError.caseError || fileErrors.length > 0) {
        if (fileErrors.length > 0) {
          caseError.fileErrors = fileErrors;
        }
        allErrors[caseIndex] = caseError;
      } else {
        allErrors[caseIndex] = {}; // No errors for this case
      }
    });

    setErrors(allErrors);

    // Call onChange with updated data
    if (onChange) {
      onChange(
        cases,
        allErrors,
        filesWithoutCase.map((f) => f.name)
      );
    }
  }, [cases, filesWithoutCase, onChange]);

  const buildCases = (files: FileList): void => {
    const folders: { [key: string]: File[] } = {};
    const filesWithoutCaseArr: File[] = [];
    const _cases: CaseUploadItem[] = [];

    // Group files by folder
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.webkitRelativePath) {
        throw new Error(
          "webkitRelativePath is not available. Please ensure you are uploading a directory."
        );
      }

      const parts = file.webkitRelativePath.split("/");
      if (parts.length < 3) {
        filesWithoutCaseArr.push(file);
        continue;
      }
      const folderName = parts[1];

      if (!folders[folderName]) {
        folders[folderName] = [];
      }

      folders[folderName].push(file);
    }

    // Create cases with ALL files (valid and invalid)
    Object.keys(folders).forEach((folderName) => {
      const filesInFolder = folders[folderName];
      if (filesInFolder.length === 0) {
        return; // Skip empty folders
      }

      const caseItem: CaseUploadItem = {
        caseName: folderName,
        filesDetected: filesInFolder.length,
        status: "queued" as FileStatus,
        files: filesInFolder.map((f) => ({
          file: f,
        })),
      };
      _cases.push(caseItem);
    });

    // Set all cases and files without case
    setCases(_cases);
    setFilesWithoutCase(filesWithoutCaseArr);
  };

  const handleFileChange = (files: FileList) => {
    buildCases(files);
  };

  // Run validation when cases or filesWithoutCase change
  useEffect(() => {
    if (cases.length > 0 || filesWithoutCase.length > 0) {
      validate();
    }
  }, [cases, filesWithoutCase, validate]);

  return (
    <Box>
      <FileUploadZone
        supportsDropping={false}
        isLoading={false}
        onChange={() => {}}
        title={Translations.upload.title}
        description={Translations.upload.description}
        buttonText={Translations.upload.button}
        renderFileList={() => <></>}
        customHandleFileChange={handleFileChange}
        supportOnlyDirectory={true}
      />
    </Box>
  );
};

export default BulkFileUpload;
